import { jsxs as _jsxs, jsx as _jsx } from "@emotion/react/jsx-runtime";
import FormatCrypto from "@hl/base-components/lib/FormatCrypto";
import useMantineMediaQueries from "@hl/base-components/lib/hooks/useMantineMediaQueries";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { Box, Stack, Text, useMantineTheme } from "@mantine/core";
import dayjs from "dayjs";
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, ComposedChart, Bar, ResponsiveContainer, } from "recharts";
import { getCurrencySymbol } from "../../../utils/currency";
import useCollectionHistoricStats from "../hooks/useCollectionHistoricStats";
// Helper function to add ordinal suffix to day
const formatDateWithSuffix = (date) => {
    const day = parseInt(date, 10);
    if (isNaN(day))
        return date;
    const suffix = day % 10 === 1 && day !== 11
        ? "ST"
        : day % 10 === 2 && day !== 12
            ? "ND"
            : day % 10 === 3 && day !== 13
                ? "RD"
                : "TH";
    return `${day}${suffix}`;
};
const CustomTooltip = ({ active, payload, label, coordinate, chainId, }) => {
    const theme = useMantineTheme();
    if (active && payload && payload.length && coordinate) {
        const data = payload[0].payload;
        const date = dayjs(label);
        return (_jsx(Box, { p: "sm", pos: "absolute", style: {
                top: coordinate.y - 70,
                left: coordinate.x,
                transform: "translate(-50%, -100%)",
                backgroundColor: theme.colors.invertedPrimaryBackground[0],
                borderRadius: 12,
                pointerEvents: "none",
                whiteSpace: "nowrap",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)", // Add shadow for better visibility
            }, children: _jsxs(Stack, { spacing: 4, align: "center", children: [_jsxs(Text, { size: "xs", color: theme.colors.invertedPrimaryText[0], sx: { letterSpacing: 0.96 }, fw: WEIGHT_BOLD, children: [date.format("MMM "), formatDateWithSuffix(date.format("D")), date.format(" YYYY")] }), _jsxs(Text, { size: "xs", color: theme.colors.invertedPrimaryText[0], children: ["Avg. Price: ", _jsx(FormatCrypto, { amount: data.floor_sell_value, onlyText: true }), getCurrencySymbol(chainId)] }), _jsxs(Text, { size: "xs", color: theme.colors.invertedPrimaryText[0], children: ["Volume: ", _jsx(FormatCrypto, { amount: data.volume, onlyText: true }), getCurrencySymbol(chainId)] })] }) }));
    }
    return null;
};
const formatTimestamp = (timestamp) => {
    return timestamp ? dayjs(timestamp).format("MMM D") : "";
};
const VolumeAndPriceChart = ({ collection: { address, chainId, marketplaceId }, disabled, }) => {
    var _a;
    const reservoirCollectionId = (marketplaceId === null || marketplaceId === void 0 ? void 0 : marketplaceId.split(/:/).slice(1).join(":")) || address;
    const { isMobile } = useMantineMediaQueries({ withInitialValues: true });
    const theme = useMantineTheme();
    const query = !disabled
        ? { id: reservoirCollectionId, limit: 31 }
        : undefined;
    const { data: stats, isLoading } = useCollectionHistoricStats(query, undefined, chainId);
    const statsNormalized = stats === null || stats === void 0 ? void 0 : stats.map((stat) => ({
        ...stat,
        timestamp: stat.timestamp ? stat.timestamp * 1000 : 0,
    })).reverse();
    if (disabled || (((_a = statsNormalized === null || statsNormalized === void 0 ? void 0 : statsNormalized.length) !== null && _a !== void 0 ? _a : 0) < 3 && !isLoading))
        return null;
    return (_jsx(ResponsiveContainer, { width: "100%", height: 400, children: _jsxs(ComposedChart, { data: statsNormalized, children: [_jsx(CartesianGrid, { horizontal: true, vertical: false, stroke: theme.fn.rgba(theme.colors.primaryText[0], 0.1), strokeDasharray: "3" }), _jsx(XAxis, { dataKey: "timestamp", tickFormatter: (value, index) => index % (isMobile ? 8 : 5) === 0 ? formatTimestamp(value) : "", interval: "preserveEnd" // Ensure last tick is always shown
                    , allowDataOverflow: true, padding: { right: 10 }, tick: {
                        fontSize: 14,
                        fontWeight: 500,
                        fill: theme.colors.primaryText[0],
                        dy: 10, // Increase gap between X-axis labels and grid
                    }, tickLine: false, axisLine: false }), _jsx(YAxis, { yAxisId: "right", orientation: "right", tick: {
                        fontSize: 14,
                        fontWeight: WEIGHT_BOLD,
                        fill: theme.colors.primaryText[0],
                    }, tickLine: false, axisLine: false, label: {
                        value: "Avg. Price (ETH)",
                        angle: -90,
                        position: "insideRight",
                        style: {
                            fontSize: 14,
                            fontWeight: WEIGHT_BOLD,
                            fill: theme.colors.primaryText[0],
                            textAnchor: "start",
                        },
                    } }), _jsx(YAxis, { yAxisId: "left", orientation: "left", width: 30, tick: false, tickLine: false, axisLine: false, label: {
                        value: "Volume (ETH)",
                        angle: -90,
                        position: "insideLeft",
                        style: {
                            fontSize: 14,
                            fontWeight: 500,
                            fill: theme.colors.primaryText[0],
                            textAnchor: "start",
                        },
                    } }), _jsx(Tooltip, { content: _jsx(CustomTooltip, { chainId: String(chainId !== null && chainId !== void 0 ? chainId : 1) }), position: { y: 0 } }), _jsx(Bar, { yAxisId: "left", dataKey: "volume", fill: theme.colors.tertiaryBackground[0] }), _jsx(Line, { yAxisId: "right", type: "monotone", dataKey: "floor_sell_value", stroke: theme.colors.primaryText[0], strokeWidth: 2, dot: { r: 0 }, activeDot: { r: 6, strokeWidth: 2 } })] }) }));
};
export default VolumeAndPriceChart;
